/**
 * OPERATE — semantic workers, the scorecard, the runtime, and the public API.
 *
 * The machinery's own health. A reviewer reads these; an operator lives in them.
 */

import { useState } from "react";
import { checkResultCanSupportReceipt } from "@excellent/core/verification-product-model";
import {
  JUDGMENTS,
  METRICS,
  METRIC_TONE,
  RUNTIME_CHECKS,
  SEMANTIC_ROUTE_LABEL,
  TENANT,
  WORKERS,
  control,
  fmtDateTime,
  fmtRelative,
  type Metric,
} from "./data";
import { href } from "./router";
import {
  Badge,
  Button,
  Callout,
  Card,
  Code,
  Empty,
  Field,
  Fields,
  Grid,
  Icon,
  PageShell,
  Section,
  Segmented,
  SearchBox,
  Stat,
  Table,
  Verdict,
} from "./ui";
import type { Column } from "./ui";
import { useStore } from "./store";

/* ══════════════════════════════════════════════════════════════════════════
   SEMANTIC WORKERS
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Semantic workers.
 *
 * ── The headline is a constraint, not a capability ──
 *
 * Every other AI-in-compliance product leads with what its model can do. This page
 * leads with what the model is not permitted to do: issue an authoritative PASS.
 * That is the honest framing and the commercially useful one — the reason a buyer
 * can trust a receipt is precisely that no model's opinion is load-bearing in it.
 * A model here reads evidence and cites it; a human or a deterministic check
 * decides.
 */

/**
 * The receipt-support rule, exercised in all three authorities.
 *
 * `status`, `evidenceArtifactIds` and `citationRefs` are IDENTICAL across the
 * three cases — every one is a PASS carrying evidence and a citation. The single
 * variable is `authority`. That isolation is deliberate: if the cases differed in
 * several fields, a reader could not tell whether "semantic cannot support a
 * receipt" was the RULE or just a missing citation.
 */
const RECEIPT_SUPPORT_BASE = {
  status: "PASS" as const,
  evidenceArtifactIds: ["ev_1"],
  citationRefs: ["cite_1"],
};

const RECEIPT_SUPPORT_CASES = [
  {
    label: "deterministic check, PASS",
    canSupport: checkResultCanSupportReceipt({ ...RECEIPT_SUPPORT_BASE, authority: "deterministic" }),
  },
  {
    label: "human check, PASS",
    canSupport: checkResultCanSupportReceipt({ ...RECEIPT_SUPPORT_BASE, authority: "human" }),
  },
  {
    label: "SEMANTIC (model) check, PASS",
    canSupport: checkResultCanSupportReceipt({ ...RECEIPT_SUPPORT_BASE, authority: "semantic" }),
  },
];

export function WorkersPage({ onNavigate }: { onNavigate: (to: string) => void }) {
  const store = useStore();
  const semantic = WORKERS.filter((w) => w.role === "semantic-check");
  const infra = WORKERS.filter((w) => w.role !== "semantic-check");

  return (
    <PageShell
      wide
      eyebrow="Operate"
        title="Semantic workers"
        lede="Model-based checkers that read evidence a deterministic rule cannot parse — a policy document, a free-text disposition, a screenshot. They produce cited judgments, never verdicts."
    >

      <Callout tone="waived" icon="lock" title="A model can never issue an authoritative PASS in this system.">
        The worker's <span className="vp-mono">authoritativeStatus</span> field is typed to accept only{" "}
        <span className="vp-mono">FAIL</span> or <span className="vp-mono">UNKNOWN</span>. A judgment of
        "support" — the model believes the evidence backs the control — routes to{" "}
        <span className="vp-mono">cited_support_needs_review</span> and waits for a human. This is not a policy
        setting you can turn off; it is the shape of the type, and it is why a receipt is worth signing.
      </Callout>

      {/* The claim above, DEMONSTRATED rather than asserted. */}
      <Section
        title="The rule, run"
        note="checkResultCanSupportReceipt from @excellent/core/verification-product-model — the shipped rule, not a restatement of it."
      >
        <Card>
          <ul>
            {RECEIPT_SUPPORT_CASES.map((c) => (
              <li key={c.label}>
                <strong>{c.label}</strong> —{" "}
                <span className="vp-mono">{c.canSupport ? "can support a receipt" : "CANNOT support a receipt"}</span>
              </li>
            ))}
          </ul>
          <p className="vp-row-sub">
            Every case is a PASS with evidence and citations attached. The only variable is{" "}
            <span className="vp-mono">authority</span> — so the difference you see is the rule itself,
            not a missing field. If the semantic row ever reads &ldquo;can support&rdquo;, the callout
            above has become false and this page says so before a customer discovers it.
          </p>
        </Card>
      </Section>

      <Grid cols={4}>
        <Stat
          label="Judgments (24h)"
          value={semantic.reduce((s, w) => s + w.succeeded24h, 0)}
          foot={`${semantic.reduce((s, w) => s + w.failed24h, 0)} failed and requeued`}
        />
        <Stat
          label="Authoritative passes issued"
          value="0"
          tone="pass"
          foot="Structurally impossible, not merely absent"
        />
        <Stat
          label="Model boundary"
          value="In-boundary"
          tone="pass"
          foot="Open weights, inside your runtime. No closed-weight model is reachable."
        />
        <Stat
          label="Queued"
          value={WORKERS.reduce((s, w) => s + w.queued, 0)}
          foot="Across all worker roles"
        />
      </Grid>

      <Section title="Semantic checkers" note="Calibration state matters — an uncalibrated model's output routes to review regardless of what it says.">
        <div className="vp-stack" data-gap="sm">
          {semantic.map((w) => (
            <Card
              key={w.id}
              title={
                <span className="vp-inline">
                  <span className="vp-mono">{w.model.name}</span>
                  <span className="vp-muted" style={{ fontWeight: 400 }}>
                    {w.model.version}
                  </span>
                </span>
              }
              subtitle={
                <>
                  <span className="vp-mono">{w.id}</span> · heartbeat {fmtRelative(w.lastHeartbeatAt)}
                </>
              }
              actions={
                <>
                  {w.inBoundary ? (
                    <Badge tone="pass" glyph="🔒">
                      in-boundary
                    </Badge>
                  ) : (
                    <Badge tone="fail" glyph="✕">
                      external
                    </Badge>
                  )}
                  <Badge tone={w.calibrated ? "pass" : "unknown"} glyph={w.calibrated ? "✓" : "?"}>
                    {w.calibrated ? "calibrated" : "uncalibrated"}
                  </Badge>
                  <Badge tone={w.status === "healthy" ? "pass" : "fail"}>{w.status}</Badge>
                </>
              }
            >
              <Fields>
                <Field label="Provider">{w.model.provider}</Field>
                <Field label="Queued">{w.queued}</Field>
                <Field label="Last 24h">
                  {w.succeeded24h} judgments, {w.failed24h} failed
                </Field>
                <Field label="Calibration">
                  {w.calibrated ? (
                    "Scored against the frozen eval set; its judgments may be dispatched."
                  ) : (
                    <span>
                      Not yet scored against the frozen eval set. It will accept work, but every result routes
                      to <span className="vp-mono">uncalibrated_model_needs_review</span> — a new model version
                      cannot inherit its predecessor's trust.
                    </span>
                  )}
                </Field>
              </Fields>
            </Card>
          ))}
        </div>
      </Section>

      <Section title="Recent judgments" note="Every one carries its route and its citations, or explains why it has none.">
        <div className="vp-stack" data-gap="sm">
          {JUDGMENTS.map((j) => {
            const c = control(j.controlId);
            return (
              <Card
                key={j.id}
                title={
                  <span className="vp-inline">
                    <Badge tone={j.judgment === "support" ? "pass" : j.judgment === "conflict" ? "fail" : "unknown"}>
                      {j.judgment}
                    </Badge>
                    <span>{c?.ref} — {c?.title}</span>
                  </span>
                }
                subtitle={
                  <>
                    <span className="vp-mono">{j.id}</span> · {j.promptVersion} · {fmtDateTime(j.createdAt)}
                  </>
                }
                actions={
                  <>
                    <Badge tone="waived">{SEMANTIC_ROUTE_LABEL[j.route]}</Badge>
                    <Badge tone="neutral" glyph="→">
                      authoritative: {j.authoritativeStatus}
                    </Badge>
                  </>
                }
                footer={
                  <>
                    <span>
                      {j.citations.length} citation{j.citations.length === 1 ? "" : "s"} ·{" "}
                      {j.citations.length === 0
                        ? "no quote means no grounding, so the result cannot support anything"
                        : "each quote resolves to a row in a hashed artifact"}
                    </span>
                    <span style={{ marginLeft: "auto" }}>
                      <Button
                        size="sm"
                        variant="ghost"
                        icon="arrow-right"
                        onClick={() => onNavigate(href("controls", j.controlId))}
                      >
                        Open the control
                      </Button>
                    </span>
                  </>
                }
              >
                <p style={{ margin: "0 0 calc(var(--spacing) * 2.5)", fontSize: "var(--type-body-size)", lineHeight: "var(--type-prose-leading)", color: "var(--ink-2)" }}>
                  {j.rationale}
                </p>
                {j.citations.map((cit, i) => (
                  <div key={i} className="vp-tl-note" style={{ fontFamily: "var(--mono)", fontSize: "var(--type-subtext-size)" }}>
                    {cit.quote}
                  </div>
                ))}
              </Card>
            );
          })}
        </div>
      </Section>

      <Section title="Infrastructure workers" note="Deterministic. No model involved.">
        <Card flush>
          <Table
            columns={[
              { key: "id", header: "Worker", primary: true, render: (w) => <span className="vp-mono">{w.id}</span> },
              { key: "role", header: "Role", render: (w) => w.role },
              {
                key: "status",
                header: "Status",
                render: (w) => <Badge tone={w.status === "healthy" ? "pass" : "fail"}>{w.status}</Badge>,
              },
              {
                key: "hb",
                header: "Heartbeat",
                render: (w) => <span className="vp-mono">{fmtRelative(w.lastHeartbeatAt)}</span>,
              },
              {
                key: "job",
                header: "Active job",
                render: (w) =>
                  w.activeJobId ? <span className="vp-mono">{w.activeJobId}</span> : <span className="vp-muted">idle</span>,
              },
              { key: "q", header: "Queued", render: (w) => <span className="vp-mono">{w.queued}</span> },
              {
                key: "24h",
                header: "Last 24h",
                render: (w) => (
                  <span className="vp-mono">
                    {w.succeeded24h} ok / {w.failed24h} failed
                  </span>
                ),
              },
            ]}
            rows={infra}
            rowKey={(w) => w.id}
          />
        </Card>
      </Section>

      <div style={{ marginTop: "calc(var(--spacing) * 5)" }}>
        <Button icon="refresh" onClick={() => store.toast("Prototype: the eval set would re-run against wkr_sem_2.")}>
          Calibrate wkr_sem_2
        </Button>
      </div>
    </PageShell>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   SCORECARD
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * The scorecard.
 *
 * Nine metrics, and the design decision is that each one carries its NOTE inline
 * rather than in a tooltip. A metric without its explanation is a number someone
 * will optimise blindly — and two of these (override rate, silent approve rate)
 * measure whether the humans are doing the job honestly, which is not something to
 * hide behind a hover.
 */
export function ScorecardPage({ onNavigate }: { onNavigate: (to: string) => void }) {
  const failing = METRICS.filter((m) => m.status === "fail");
  const warning = METRICS.filter((m) => m.status === "warn");

  const columns: Column<Metric>[] = [
    {
      key: "label",
      header: "Metric",
      primary: true,
      sortValue: (m) => m.label,
      render: (m) => (
        <>
          {m.label}
          <div className="vp-mono" style={{ color: "var(--ink-3)", marginTop: "calc(var(--spacing) * 0.5)", fontSize: "var(--type-subtext-size)" }}>
            {m.id}
          </div>
        </>
      ),
    },
    {
      key: "value",
      header: "Now",
      sortValue: (m) => m.numeric,
      render: (m) => (
        <Badge tone={METRIC_TONE[m.status]} size="lg">
          {m.value}
        </Badge>
      ),
    },
    { key: "target", header: "Target", render: (m) => <span className="vp-mono">{m.target}</span> },
    {
      key: "dir",
      header: "Direction",
      render: (m) => (m.direction === "higher-is-better" ? "higher is better" : "lower is better"),
    },
    {
      key: "note",
      header: "What it means here",
      render: (m) => <span style={{ color: "var(--ink-2)" }}>{m.note}</span>,
    },
  ];

  return (
    <PageShell
      wide
      eyebrow="Operate"
        title="Scorecard"
        lede="Nine metrics decide whether this workspace's verification is trustworthy enough to build a commercial claim on. Two of them measure the humans, not the machine."
    >

      <Grid cols={3}>
        <Stat
          label="Off target"
          value={failing.length}
          tone={failing.length > 0 ? "fail" : "pass"}
          foot={failing.map((m) => m.label).join(" · ") || "None"}
        />
        <Stat
          label="Warning"
          value={warning.length}
          tone={warning.length > 0 ? "unknown" : "pass"}
          foot={warning.map((m) => m.label).join(" · ") || "None"}
        />
        <Stat
          label="Release ready"
          value={failing.length === 0 ? "Yes" : "No"}
          tone={failing.length === 0 ? "pass" : "fail"}
          foot={
            failing.length === 0
              ? "Every required metric is inside its threshold"
              : "A metric off target blocks the release gate, not just the dashboard"
          }
        />
      </Grid>

      <div style={{ marginTop: "calc(var(--spacing) * 4.5)" }}>
        <Callout tone="neutral" icon="info" title="Two of these are integrity metrics.">
          <span className="vp-mono">override_rate</span> counts how often a human overrode the engine, and{" "}
          <span className="vp-mono">silent_approve_rate</span> counts findings closed with no written
          rationale. The second is a hard zero: the review form has no path that produces one. A rising
          override rate is not automatically bad — but it is always worth reading, because it is what a
          rubber-stamping culture looks like in data.
        </Callout>
      </div>

      <Card flush>
        <Table columns={columns} rows={METRICS} rowKey={(m) => m.id} />
      </Card>

      <div style={{ marginTop: "calc(var(--spacing) * 4.5)" }}>
        <Callout tone="unknown" icon="alert" title="Why verified coverage is 37.5% and not higher.">
          Five of eight controls cannot produce a PASS receipt today. Two are genuine failures with open
          findings. Two are UNKNOWN because their evidence sources are broken — a revoked Vanta token and a
          missing Jira scope — and one has never run because nothing is mapped to it. Only the first two will
          be fixed by reviewing findings; the other three are source and mapping work.
          <div className="vp-inline" style={{ marginTop: "calc(var(--spacing) * 2.5)" }}>
            <Button size="sm" variant="outline" icon="plug" onClick={() => onNavigate(href("connectors"))}>
              Fix the sources
            </Button>
            <Button size="sm" variant="outline" icon="shield" onClick={() => onNavigate(href("controls", "ctl_a12"))}>
              Map A1.2
            </Button>
          </div>
        </Callout>
      </div>
    </PageShell>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   RUNTIME
   ══════════════════════════════════════════════════════════════════════════ */

export function RuntimePage() {
  const store = useStore();
  const failing = RUNTIME_CHECKS.filter((c) => c.status === "fail");
  const warning = RUNTIME_CHECKS.filter((c) => c.status === "warn");

  return (
    <PageShell
      eyebrow="Operate"
        title="Runtime & keys"
        lede="Where evidence physically lives, who holds the signing key, and whether a verdict can be checked with us switched off."
        actions={
          <Button icon="refresh" onClick={() => store.toast("Runtime doctor re-run — 5 pass, 1 warn, 0 fail.")}>
            Run the doctor
          </Button>
        }
    >

      <Grid cols={3}>
        <Stat
          label="Deployment mode"
          value={TENANT.runtimeMode}
          tone="accent"
          foot="Evidence never leaves this boundary"
        />
        <Stat
          label="Key custody"
          value={TENANT.keyCustody}
          tone="pass"
          foot="Excellent cannot sign a receipt for you"
        />
        <Stat
          label="Doctor"
          value={failing.length === 0 ? (warning.length === 0 ? "All pass" : `${warning.length} warn`) : `${failing.length} fail`}
          tone={failing.length > 0 ? "fail" : warning.length > 0 ? "unknown" : "pass"}
          foot={`${RUNTIME_CHECKS.length} checks`}
        />
      </Grid>

      <Section title="Runtime doctor" note="Each check states what it verified, not just that it passed.">
        <Card flush>
          <div className="vp-rows">
            {RUNTIME_CHECKS.map((c) => (
              <div key={c.id} className="vp-row" style={{ alignItems: "flex-start" }}>
                <span style={{ flex: "0 0 auto", paddingTop: "calc(var(--spacing) * 0.5)" }}>
                  <Badge
                    tone={c.status === "pass" ? "pass" : c.status === "warn" ? "unknown" : "fail"}
                    glyph={c.status === "pass" ? "✓" : c.status === "warn" ? "!" : "✕"}
                  >
                    {c.status}
                  </Badge>
                </span>
                <span className="vp-row-main">
                  <span className="vp-row-title">{c.label}</span>
                  <span className="vp-row-sub">{c.detail}</span>
                </span>
              </div>
            ))}
          </div>
        </Card>
      </Section>

      <Section title="What crosses the boundary" note="The complete list. Anything not here does not leave.">
        <Grid cols={2}>
          <Card title="Stays inside">
            <div className="vp-rows" style={{ margin: "calc(var(--spacing) * -4)" }}>
              {[
                "Raw evidence artifacts — every byte collected from every source",
                "Observations derived from those artifacts",
                "Your connector credentials and their scopes",
                "The signing private key (held in your KMS, never exported)",
                "Semantic worker model weights and every prompt sent to them",
                "Review rationales and the identities of the people who wrote them",
              ].map((t) => (
                <div key={t} className="vp-row">
                  <span style={{ flex: "0 0 auto", color: "var(--pass)" }}>
                    <Icon name="lock" size={15} />
                  </span>
                  <span className="vp-row-main">
                    <span className="vp-row-title" style={{ fontWeight: 400 }}>
                      {t}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Crosses out">
            <div className="vp-rows" style={{ margin: "calc(var(--spacing) * -4)" }}>
              {[
                "Receipt hashes and the verdict literal (PASS / FAIL / UNKNOWN)",
                "Control identifiers and run timestamps",
                "Aggregate metric counts for the scorecard",
                "Software version and licence heartbeat",
              ].map((t) => (
                <div key={t} className="vp-row">
                  <span style={{ flex: "0 0 auto", color: "var(--ink-3)" }}>
                    <Icon name="external" size={15} />
                  </span>
                  <span className="vp-row-main">
                    <span className="vp-row-title" style={{ fontWeight: 400 }}>
                      {t}
                    </span>
                  </span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: "calc(var(--spacing) * 3.5)" }}>
              <Callout tone="neutral" icon="info">
                On <span className="vp-mono">local-offline</span> mode nothing crosses at all — the sync agent
                is not installed, and receipts are carried out by hand as replay bundles.
              </Callout>
            </div>
          </Card>
        </Grid>
      </Section>

      <Section title="Key rotation">
        <Card>
          <Fields>
            <Field label="Active key">
              <span className="vp-mono">key_nw_prod_2026q3</span> — Ed25519, in AWS KMS as{" "}
              <span className="vp-mono">alias/northwind-verification</span>
            </Field>
            <Field label="Previous key">
              <span className="vp-mono">key_nw_prod_2026q2</span> — retained, because receipts signed with it
              must stay verifiable forever
            </Field>
            <Field label="Last rotated">{fmtDateTime("2026-07-01T00:00:00Z")} · 47 days ago</Field>
            <Field label="Policy">
              Every 90 days. Rotation does not invalidate old receipts — each one records the key that signed
              it, and the public half of every retired key ships in the pack.
            </Field>
          </Fields>
          <div style={{ marginTop: "calc(var(--spacing) * 3.5)" }}>
            <Button icon="refresh" onClick={() => store.toast("Prototype: the rotation runbook would open.")}>
              Schedule the next rotation
            </Button>
          </div>
        </Card>
      </Section>
    </PageShell>
  );
}
